import { useContext, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Card,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Switch,
  Typography,
} from '@mui/material';

import FilterBar from '../../../common/filter-bar/filter-bar';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import axios, {
  AxiosRequestConfig,
  AxiosResponse,
  CancelTokenSource,
} from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { buildQueryParams } from '../../../common/filter-bar/filter-bar.utils';
import { PaginationContext } from '../../../../contexts/pagination-context';
import { IFilterOption } from '../../../common/filter-bar/types';
import { IPickupTask } from '../../../../interfaces/delivery-company/tasks';
import DynamicCardListing from './pickup-card-listing';
import { useSearchParams } from 'react-router-dom';
import {
  ALL,
  FINISHED,
  IN_PROGRESS,
  PENDING,
  TASK_STATE_CATEGORY,
} from '../filter_utils';
import { DriverRowsPerPage } from '../../driver-constant';

function DriverPickupsListing() {
  const [searchParams, setSearchParams] = useSearchParams();
  const pickupStateFilter = searchParams.get('status') || PENDING;

  const changePickupStateFilter = (newFilter: TASK_STATE_CATEGORY) => {
    setSearchParams({ status: newFilter });
  };
  const [criticalOnly, setCriticalOnly] = useState<boolean>(false);

  const [message, setMessage] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [pickupList, setPickupList] = useState<IPickupTask[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [totalCount, setTotalCount] = useState(0);
  const [response, setResponse] = useState<AxiosResponse>();

  const { user, config } = useContext(AuthContext);

  const relevant_fields = [
    'id',
    'package_count',
    'customer',
    'merchant',
    'customer_location',
    'merchant_location',
    'status',
    'workflow',
    'is_critical',
    'notes',
    'failure_reason',
    'creation_date',
  ];

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: DriverRowsPerPage,
    searchColumn: '',
    searchValue: '',
    searchOperation: 'equals',
  });

  const loadMorePickupTask = () => {
    setController((prev) => ({
      ...prev,
      rowsPerPage: prev.rowsPerPage + DriverRowsPerPage,
    }));
  };

  const [filters, setFilters] = useState<IFilterOption[]>([]);

  const cancelTokenSourceRef = useRef<CancelTokenSource | null>(null);

  const reloadTasks = async () => {
    if (cancelTokenSourceRef.current) {
      cancelTokenSourceRef.current.cancel(
        'Operation canceled due to new request'
      );
    }

    // Create new cancel token
    const cancelTokenSource = axios.CancelToken.source();
    cancelTokenSourceRef.current = cancelTokenSource;

    setIsLoading(true);
    setMessage('');
    setResponse(undefined);
    let endpoint = `${API_ENDPOINT}/api/v1/pickup-tasks/?page_size=${(controller.page + 1) * controller.rowsPerPage}&fields=${relevant_fields.join(',')}`;
    endpoint += `&status=${pickupStateFilter}`;
    endpoint += buildQueryParams(filters);

    if (criticalOnly) endpoint += `&is_critical=True`;
    console.log(endpoint);
    try {
      const requestConfig: AxiosRequestConfig = {
        ...config,
        cancelToken: cancelTokenSource.token,
      };
      const response = await axios.get<{
        results: IPickupTask[];
        count: number;
      }>(endpoint, requestConfig);

      setTotalCount(response.data.count);
      console.log('setting list');
      setPickupList(response.data.results);
      setResponse(response);
      console.log(response);
    } catch (e) {
      if (axios.isCancel(e)) {
        console.log('Request canceled:', e.message);
        return;
      }

      if (axios.isAxiosError(e)) {
        setMessage(
          'Failed to get your pickup tasks: ' + e.response?.statusText
        );
        setResponse(e.response);
      } else setMessage('Failed to get your pickup tasks');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    reloadTasks();
    return () => {
      if (cancelTokenSourceRef.current) {
        cancelTokenSourceRef.current.cancel('Component unmounted');
      }
    };
  }, [controller, filters, pickupStateFilter, refreshKey, criticalOnly]);

  const resetPaginationControls = () => {
    setController({
      ...controller,
      page: 0,
    });
  };
  const filterQueryAction = (filters: IFilterOption[]) => {
    resetPaginationControls();
    setFilters(filters);
  };

  return (
    <>
      <FilterBar
        filterQueryAction={filterQueryAction}
        currentlySelectedFilters={filters}
        filterKeys={['customer', 'merchant', 'district']}
        headless
      />
      <br />

      <div style={{ textAlign: 'center', width: '100%' }}>
        <Switch
          checked={criticalOnly}
          onChange={() => setCriticalOnly(!criticalOnly)}
        ></Switch>
        Critical Tasks Only
      </div>

      <FormControl component="fieldset" sx={{ marginBottom: 2 }} fullWidth>
        <RadioGroup
          row
          aria-label="state"
          name="state"
          value={pickupStateFilter}
          onChange={(e) =>
            changePickupStateFilter(e.target.value as TASK_STATE_CATEGORY)
          }
          sx={{ display: 'flex', gap: 0, justifyContent: 'center' }}
        >
          <FormControlLabel
            value={PENDING}
            control={<Radio size="small" />}
            label={'Awaiting Pickup'}
          />
          <FormControlLabel
            value={IN_PROGRESS}
            control={<Radio size="small" />}
            label={'In Progress'}
          />
          <FormControlLabel
            value={FINISHED}
            control={<Radio size="small" />}
            label={'Finished'}
          />
          <FormControlLabel
            value={ALL}
            control={<Radio size="small" />}
            label={'All'}
          />
        </RadioGroup>
      </FormControl>
      <DynamicCardListing
        loadMoreTasks={loadMorePickupTask}
        isLoading={isLoading}
        tasks={pickupList}
        statusFilter={pickupStateFilter}
        reloadList={reloadTasks}
        totalCount={totalCount}
        updateTasks={(mutator: (task: IPickupTask) => IPickupTask) =>
          setPickupList(pickupList.map((p) => mutator(p)))
        }
      />
    </>
  );
}

export default DriverPickupsListing;
